// src/components/Admin/ItemManager/ItemUsageLog.jsx
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Paper, Button, Stack, TextField, Typography, Pagination,
  useTheme, useMediaQuery, Divider,
} from '@mui/material';
import api from '../../../api/axios';
import ConfirmMemoModal from './ConfirmMemoModal';

export default function ItemUsageLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedDate, setSelectedDate] = useState('');
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* ───── 데이터 로드 ───── */
  const fetchLogs = () => {
    setLoading(true);
    api.get('/api/rewards/item-admin/pending/')
      .then((res) => setLogs(res.data.pending || []))
      .catch(() => alert('대기 쿠폰을 불러오는 데 실패했습니다.'))
      .finally(() => setLoading(false));
  };
  useEffect(fetchLogs, []);

  /* ───── 필터링 (useMemo) ───── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      const matchDate = !selectedDate || (l.used_at || '').startsWith(selectedDate);
      const matchSearch =
        !q ||
        (l.username || '').toLowerCase().includes(q) ||
        (l.item_name || '').toLowerCase().includes(q);
      return matchDate && matchSearch;
    });
  }, [logs, selectedDate, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  // ✅ page 범위 밖이면 자동 보정
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginated = useMemo(() => (
    filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage)
  ), [filtered, page]);

  /* ───── 처리 핸들러 ───── */
  const handleApprove = (log) => {
    document.activeElement?.blur();
    setSelectedLog(log);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleRefund = (log) => {
    if (!window.confirm('이 쿠폰을 환불하시겠습니까?')) return;
    api.post(`/api/rewards/item-admin/confirm/${log.id}/`, { action: 'refund', memo: '' })
      .then((res) => { alert(res.data.message); fetchLogs(); })
      .catch(() => alert('환불 처리에 실패했습니다.'));
  };

  const handleSubmitMemo = (memo) => {
    const method = isEditing ? 'patch' : 'post';
    api[method](`/api/rewards/item-admin/confirm/${selectedLog.id}/`, {
      action: 'approve',
      memo,
    })
      .then((res) => { alert(res.data.message); setModalOpen(false); fetchLogs(); })
      .catch(() => alert('승인 처리에 실패했습니다.'));
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* 검색 바 */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={isMobile ? 'stretch' : 'center'}
      >
        <TextField
          size="small"
          label="검색 (닉네임/아이템명)"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          fullWidth={isMobile}
        />
        <TextField
          type="date"
          size="small"
          label="날짜 선택"
          InputLabelProps={{ shrink: true }}
          value={selectedDate}
          onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
          fullWidth={isMobile}
        />
        <Typography
          variant="body2"
          sx={{
            ml: isMobile ? 0 : 'auto',
            alignSelf: isMobile ? 'flex-end' : 'center',
            color: 'text.secondary',
          }}
        >
          총 {filtered.length}건
        </Typography>
      </Stack>

      {filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography sx={{ color: 'text.secondary' }}>
            현재 승인 대기 중인 쿠폰이 없습니다.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* ✅ 모바일 = 카드형 */}
          {isMobile ? (
            <Stack spacing={1.25}>
              {paginated.map((log) => (
                <Paper key={log.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                    {log.item_name}
                  </Typography>

                  <Stack spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">유저명</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{log.username}</Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">사용일시</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{log.used_at}</Typography>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 1.25 }} />

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      onClick={() => handleApprove(log)}
                    >
                      승인
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      fullWidth
                      onClick={() => handleRefund(log)}
                    >
                      환불
                    </Button>
                  </Stack>
                </Paper>
              ))}

              {totalPages > 1 && (
                <Box mt={0.5} display="flex" justifyContent="center">
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    color="primary"
                    showFirstButton
                    showLastButton
                    size="small"
                    shape="rounded"
                  />
                </Box>
              )}
            </Stack>
          ) : (
            /* ✅ PC = 테이블 유지 */
            <>
              <Paper variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>유저명</TableCell>
                      <TableCell>쿠폰명</TableCell>
                      <TableCell>사용일시</TableCell>
                      <TableCell>처리</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginated.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.username}</TableCell>
                        <TableCell>{log.item_name}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.used_at}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleApprove(log)}
                            >
                              승인
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              onClick={() => handleRefund(log)}
                            >
                              환불
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {totalPages > 1 && (
                <Box mt={2} display="flex" justifyContent="center">
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    color="primary"
                    showFirstButton
                    showLastButton
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* 메모 모달 */}
      <ConfirmMemoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitMemo}
        initialMemo={selectedLog?.confirmed_memo || ''}
        isEditing={isEditing}
      />
    </Box>
  );
}
